import React, { useState } from 'react';
import { XIcon, CheckIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
import Button from '../Button';
import { useWorkflow } from '../../context/WorkflowContext';
import { useApplications } from '../../context/ApplicationContext';
interface WorkflowApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  instanceId: string;
  stepId: string;
  applicationId: string;
  referenceNumber: string;
  serviceName: string;
  currentStepName: string;
}
const WorkflowApprovalModal: React.FC<WorkflowApprovalModalProps> = ({
  isOpen,
  onClose,
  instanceId,
  stepId,
  applicationId,
  referenceNumber,
  serviceName,
  currentStepName
}) => {
  const [comments, setComments] = useState('');
  const [action, setAction] = useState<'approved' | 'rejected' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    updateWorkflowStep,
    canUserApproveStep
  } = useWorkflow();
  const {
    updateApplicationStatus
  } = useApplications();
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!action) {
      setError('Please select an action (Approve or Reject)');
      return;
    }
    if (!canUserApproveStep(stepId, instanceId)) {
      setError('You do not have permission to approve this step');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      // Update the workflow step
      const updatedInstance = updateWorkflowStep(instanceId, stepId, action, comments);
      // If this was the final approval or a rejection, update the application status
      if (updatedInstance) {
        if (action === 'rejected') {
          updateApplicationStatus(applicationId, 'rejected', comments || 'Application rejected');
        } else if (action === 'approved' && updatedInstance.status === 'completed') {
          updateApplicationStatus(applicationId, 'approved', comments || 'Application approved');
        } else if (action === 'approved') {
          updateApplicationStatus(applicationId, 'processing', `Step "${currentStepName}" approved. Moving to next step.`);
        }
      }
      // Close the modal after successful submission
      onClose();
    } catch (err) {
      setError('An error occurred while processing your request');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-183">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-184">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-185">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-186"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-187">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-188">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-189">
            <div className="sm:flex sm:items-start" data-id="element-190">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-191">
                <div className="flex justify-between items-center" data-id="element-192">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-193">
                    Workflow Approval
                  </h3>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-194">
                    <XIcon className="h-5 w-5" data-id="element-195" />
                  </button>
                </div>
                <div className="mt-4" data-id="element-196">
                  <div className="bg-gray-50 p-3 rounded-md mb-4" data-id="element-197">
                    <p className="text-sm text-gray-700" data-id="element-198">
                      <span className="font-medium" data-id="element-199">Reference Number:</span>{' '}
                      {referenceNumber}
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-200">
                      <span className="font-medium" data-id="element-201">Service:</span>{' '}
                      {serviceName}
                    </p>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-202">
                      <span className="font-medium" data-id="element-203">Current Step:</span>{' '}
                      {currentStepName}
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} data-id="element-204">
                    <div className="mb-4" data-id="element-205">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-206">
                        Comments
                      </label>
                      <textarea value={comments} onChange={e => setComments(e.target.value)} className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border border-gray-300 rounded-md p-2" rows={4} placeholder="Add your comments here (optional)" data-id="element-207" />
                    </div>
                    <div className="mb-4" data-id="element-208">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-209">
                        Action
                      </label>
                      <div className="flex space-x-4" data-id="element-210">
                        <button type="button" onClick={() => setAction('approved')} className={`flex-1 py-2 px-4 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${action === 'approved' ? 'bg-green-600 text-white border-transparent hover:bg-green-700 focus:ring-green-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-green-500'}`} data-id="element-211">
                          <div className="flex items-center justify-center" data-id="element-212">
                            <CheckIcon className="h-4 w-4 mr-2" data-id="element-213" />
                            Approve
                          </div>
                        </button>
                        <button type="button" onClick={() => setAction('rejected')} className={`flex-1 py-2 px-4 border rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${action === 'rejected' ? 'bg-red-600 text-white border-transparent hover:bg-red-700 focus:ring-red-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-red-500'}`} data-id="element-214">
                          <div className="flex items-center justify-center" data-id="element-215">
                            <XCircleIcon className="h-4 w-4 mr-2" data-id="element-216" />
                            Reject
                          </div>
                        </button>
                      </div>
                    </div>
                    {error && <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md" data-id="element-217">
                        <div className="flex items-center text-red-600" data-id="element-218">
                          <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-219" />
                          <p className="text-sm" data-id="element-220">{error}</p>
                        </div>
                      </div>}
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse" data-id="element-221">
                      <Button type="submit" variant="primary" disabled={isSubmitting || !action} className="w-full sm:w-auto sm:ml-3" data-id="element-222">
                        {isSubmitting ? 'Processing...' : 'Submit Decision'}
                      </Button>
                      <Button type="button" variant="outline" onClick={onClose} className="mt-3 w-full sm:mt-0 sm:w-auto" data-id="element-223">
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default WorkflowApprovalModal;